// frontend/src/services/pushSubscriptionService.js
// Service pour gerer les subscriptions Push (PWA)

import api from '../utils/apiClient';

/**
 * Service Push Subscription
 *
 * Gere:
 * - Detection du support Push
 * - Recuperation de la cle VAPID
 * - Creation/suppression de subscription
 * - Synchronisation avec le backend
 */

// Cache de la cle publique VAPID
let cachedPublicKey = null;

/**
 * Verifie si les Push Notifications sont supportees
 * @returns {boolean}
 */
export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Verifie si on est en mode PWA standalone
 * @returns {boolean}
 */
export function isPWAMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Retourne le statut actuel de la permission Notification
 * @returns {'default' | 'granted' | 'denied'}
 */
export function getPermissionStatus() {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/**
 * Recupere la cle publique VAPID depuis le backend
 * @returns {Promise<string|null>}
 */
export async function getPublicKey() {
  if (cachedPublicKey) return cachedPublicKey;

  try {
    const data = await api.get('/push/public-key');
    cachedPublicKey = data.publicKey;
    return cachedPublicKey;
  } catch (error) {
    console.error('[Push] Erreur recuperation cle publique:', error.message);
    return null;
  }
}

/**
 * Convertit une cle VAPID base64url en Uint8Array
 * @param {string} base64String
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Detecte le type de device
 * @returns {'mobile' | 'tablet' | 'desktop'}
 */
function detectDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua);
  const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(ua);

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  return 'desktop';
}

/**
 * Recupere la subscription Push actuelle (si elle existe)
 * @returns {Promise<PushSubscription|null>}
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('[Push] Erreur recuperation subscription:', error);
    return null;
  }
}

/**
 * Cree une nouvelle subscription Push et l'enregistre sur le backend
 * @returns {Promise<{success: boolean, subscription?: PushSubscription, error?: string}>}
 */
export async function subscribePush() {
  if (!isPushSupported()) {
    return { success: false, error: 'Push non supporte sur ce navigateur' };
  }

  try {
    // 1. Demander la permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Permission refusee' };
    }

    // 2. Recuperer la cle publique VAPID
    const publicKey = await getPublicKey();
    if (!publicKey) {
      return { success: false, error: 'Cle VAPID non disponible' };
    }

    // 3. Attendre que le SW soit pret
    const registration = await navigator.serviceWorker.ready;

    // 4. Creer la subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    // 5. Envoyer au backend
    const subscriptionJSON = subscription.toJSON();
    await api.post('/push/subscribe', {
      subscription: {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
      },
      userAgent: navigator.userAgent,
      deviceType: detectDeviceType(),
    });

    // 6. Sauvegarder en local
    localStorage.setItem('pushEnabled', 'true');

    console.log('[Push] Subscription creee avec succes');
    return { success: true, subscription };
  } catch (error) {
    console.error('[Push] Erreur creation subscription:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Supprime la subscription Push locale et notifie le backend
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unsubscribePush() {
  try {
    const subscription = await getCurrentSubscription();

    if (subscription) {
      // Notifier le backend (best effort)
      try {
        await api.post('/push/unsubscribe', {
          endpoint: subscription.endpoint,
        });
      } catch (apiError) {
        console.warn('[Push] Erreur notification backend:', apiError.message);
      }

      // Supprimer localement
      await subscription.unsubscribe();
    }

    // Mettre a jour le flag local
    localStorage.setItem('pushEnabled', 'false');

    console.log('[Push] Subscription supprimee');
    return { success: true };
  } catch (error) {
    console.error('[Push] Erreur suppression subscription:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Verifie si Push est active pour l'utilisateur courant
 * @returns {Promise<boolean>}
 */
export async function isPushEnabled() {
  // Check local flag first
  const localFlag = localStorage.getItem('pushEnabled');
  if (localFlag === 'false') return false;

  // Check actual subscription
  const subscription = await getCurrentSubscription();
  return subscription !== null;
}

/**
 * Recupere le statut complet des subscriptions depuis le backend
 * @returns {Promise<object|null>}
 */
export async function getPushStatus() {
  try {
    return await api.get('/push/status');
  } catch (error) {
    console.error('[Push] Erreur recuperation statut:', error.message);
    return null;
  }
}

/**
 * Envoie une notification push de test (admin only)
 * @returns {Promise<{success: boolean, result?: object, error?: string}>}
 */
export async function sendTestPush() {
  try {
    const result = await api.post('/push/test');
    return { success: true, result };
  } catch (error) {
    console.error('[Push] Erreur envoi test:', error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Met a jour le badge de l'application via le Service Worker
 * @param {number} count
 */
export async function updateBadge(count) {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({
        type: 'SET_BADGE_COUNT',
        count,
      });
    }
  } catch (error) {
    console.warn('[Push] Erreur mise a jour badge:', error);
  }
}

/**
 * Efface le badge de l'application
 */
export async function clearBadge() {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({ type: 'CLEAR_BADGE' });
    }
  } catch (error) {
    console.warn('[Push] Erreur effacement badge:', error);
  }
}

export default {
  isPushSupported,
  isPWAMode,
  getPermissionStatus,
  getPublicKey,
  getCurrentSubscription,
  subscribePush,
  unsubscribePush,
  isPushEnabled,
  getPushStatus,
  sendTestPush,
  updateBadge,
  clearBadge,
};
